/**
 * SocialAuthButtons — Google + Apple "Continue with …" buttons for the
 * web customer login/register surfaces.
 *
 * 2026-06-28 — Emergent Managed Google hosted-redirect flow.
 *   The Google button no longer opens the Google Identity Services popup.
 *   It redirects the browser to Emergent's hosted consent page
 *   (`auth.emergentagent.com`), which handles Google consent on
 *   Emergent-owned infrastructure and returns the user to
 *   `/customer/auth/callback#session_id=<opaque>`. The callback page
 *   exchanges the session_id with our A1 adapter endpoint for a OneStamp
 *   JWT and runs the gated router.
 *
 *   No Google Cloud OAuth client id is needed on the operator side —
 *   Emergent provides the Google client at the platform/tenant level.
 *
 * Apple button is unchanged — Apple is a separate workstream and still
 * uses the operator-provisioned Services ID + Apple JS SDK.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// (Note: `auth.emergentagent.com` and `/customer/auth/callback` are the
// Emergent-platform-fixed endpoints. The CALLBACK redirect uses
// `window.location.origin` dynamically — never hardcode the host.)
const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com/';

const APPLE_SERVICES_ID =
  (process.env.REACT_APP_APPLE_SERVICES_ID || '').trim();
const APPLE_REDIRECT_URI =
  (process.env.REACT_APP_APPLE_REDIRECT_URI || '').trim();

export const GOOGLE_SIGNIN_ENABLED = true; // tenant-provided — always available
export const APPLE_SIGNIN_ENABLED = Boolean(APPLE_SERVICES_ID);

const APPLE_SDK_URL =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

function _loadAppleSdk() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.AppleID) return Promise.resolve(window.AppleID);
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${APPLE_SDK_URL}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.AppleID));
      existing.addEventListener('error', () => reject(new Error('Apple SDK load failed')));
      if (window.AppleID) resolve(window.AppleID);
      return;
    }
    const script = document.createElement('script');
    script.src = APPLE_SDK_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.AppleID);
    script.onerror = () => reject(new Error('Apple SDK load failed'));
    document.head.appendChild(script);
  });
}

// ---------- Google button ---------------------------------------------------
// Redirects the browser to Emergent Auth. On return, the callback page
// at /customer/auth/callback consumes the session_id and routes.
function GoogleContinueBtn({ busy }) {
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(() => {
    setLoading(true);
    try {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = `${window.location.origin}/customer/auth/callback`;
      window.location.href =
        `${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[socialAuth] Emergent redirect failed', err);
      setLoading(false);
    }
  }, []);

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy || loading}
      data-testid="customer-google-btn"
      className="w-full inline-flex items-center justify-center gap-2 bg-white text-neutral-900 border border-white/10 rounded-xl py-2.5 text-sm font-semibold hover:bg-neutral-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
    >
      {loading ? (
        <>
          <Loader2 size={14} className="animate-spin" /> Redirecting…
        </>
      ) : (
        <>
          <img
            src="https://developers.google.com/identity/images/g-logo.png"
            alt=""
            className="w-4 h-4"
          />
          Continue with Google
        </>
      )}
    </button>
  );
}

// ---------- Apple button ----------------------------------------------------
// Unchanged from Stage 1 — Apple is operator-provisioned (not managed by
// Emergent). When UNconfigured, tapping surfaces a user-facing error.
function AppleContinueBtn({ onSuccess, onError, busy }) {
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!APPLE_SIGNIN_ENABLED) {
      if (!warnedRef.current) {
        warnedRef.current = true;
        // eslint-disable-next-line no-console
        console.error(
          '[socialAuth] Apple button rendered but REACT_APP_APPLE_SERVICES_ID is empty.'
        );
      }
      return undefined;
    }
    let cancelled = false;
    _loadAppleSdk()
      .then((AppleID) => {
        if (cancelled || !AppleID?.auth) return;
        try {
          AppleID.auth.init({
            clientId:    APPLE_SERVICES_ID,
            redirectURI: APPLE_REDIRECT_URI || window.location.origin,
            scope:       'name email',
            usePopup:    true,
          });
          setReady(true);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('[socialAuth] Apple SDK init failed', err);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[socialAuth] Apple SDK load failed', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (!APPLE_SIGNIN_ENABLED) {
      // eslint-disable-next-line no-console
      console.error(
        '[socialAuth] Apple button tapped but REACT_APP_APPLE_SERVICES_ID is empty.'
      );
      onError('Apple sign-in is not configured yet. Please contact support.');
      return;
    }
    if (!ready || !window.AppleID?.auth) {
      onError('Apple sign-in is still initialising — please retry in a moment.');
      return;
    }
    setLoading(true);
    try {
      const data = await window.AppleID.auth.signIn();
      const idToken = data?.authorization?.id_token;
      if (!idToken) {
        onError('Apple did not return an id_token.');
        return;
      }
      const extras = {};
      if (data?.user?.email) extras.email = data.user.email;
      if (data?.user?.name) {
        extras.full_name = {
          givenName:  data.user.name.firstName || undefined,
          familyName: data.user.name.lastName || undefined,
        };
      }
      const res = await api.appleSignIn(idToken, extras);
      await onSuccess(res);
    } catch (e) {
      const code = String(e?.error || e?.code || '');
      if (code === 'popup_closed_by_user' || /cancell?ed|user_cancelled/i.test(code)) {
        return;
      }
      onError(e?.detail || e?.message || 'Apple Sign-In failed.');
    } finally {
      setLoading(false);
    }
  }, [ready, onError, onSuccess]);

  return (
    <button
      type="button"
      disabled={busy || loading}
      onClick={handleClick}
      data-testid="customer-apple-btn"
      className="w-full inline-flex items-center justify-center gap-2 bg-black text-white border border-white/10 rounded-xl py-2.5 text-sm font-semibold hover:bg-neutral-900 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
    >
      {loading ? (
        <>
          <Loader2 size={14} className="animate-spin" /> Signing in with Apple…
        </>
      ) : (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M16.365 1.43c0 1.14-.47 2.26-1.19 3.07-.78.88-2.07 1.56-3.13 1.47-.14-1.11.4-2.26 1.08-3.03.78-.88 2.14-1.55 3.24-1.51zM20.5 17.6c-.57 1.33-.84 1.93-1.57 3.1-1.03 1.63-2.48 3.66-4.28 3.67-1.6.02-2.01-1.04-4.18-1.03-2.17.01-2.62 1.05-4.22 1.03-1.8-.02-3.17-1.86-4.2-3.5-2.88-4.58-3.18-9.95-1.4-12.81 1.26-2.03 3.25-3.22 5.12-3.22 1.9 0 3.09 1.04 4.66 1.04 1.52 0 2.45-1.04 4.66-1.04 1.66 0 3.42.9 4.67 2.46-4.1 2.25-3.43 8.1.74 10.3z"/>
          </svg>
          Continue with Apple
        </>
      )}
    </button>
  );
}

// ---------- Public wrapper --------------------------------------------------
export function SocialAuthButtons({ onSuccess, onError, busy = false }) {
  return (
    <>
      <div className="my-5 flex items-center gap-3" data-testid="social-divider">
        <div className="flex-1 h-px bg-white/8" />
        <div className="text-[10px] uppercase tracking-widest text-white/40">
          OR CONTINUE WITH
        </div>
        <div className="flex-1 h-px bg-white/8" />
      </div>

      <div className="flex flex-col gap-3">
        {/* Google button is handled entirely by a full-page redirect to
            Emergent Auth. It needs no onSuccess/onError wiring — the
            callback page at /customer/auth/callback drives the auth-store
            when the browser returns. */}
        <GoogleContinueBtn busy={busy} />
        <AppleContinueBtn
          onSuccess={onSuccess}
          onError={onError}
          busy={busy}
        />
      </div>
    </>
  );
}
